import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import DesktopLayout from '../layouts/DesktopLayout';
import axios from 'axios';
import secureApiService from '../services/secureApi';
import taskApi from '../services/taskApi';
import { formatDate } from '../utils/dateFormatter';
import AdminDashboardLockTable from '../components/AdminDashboardLockTable';
import ArrivalReadinessCard from '../components/ArrivalReadinessCard';
import DepartureReadinessCard from '../components/DepartureReadinessCard';
import ErrorBoundary from '../components/ErrorBoundary';
import { useUnresolvedAlerts } from '../hooks/useAlerts';
import '../styles/AdminDashboard.css';

// Alert-type filter constants — kept module-level so the helper and the
// JSX cards read the same source of truth. Both counts derive from the
// same /api/v1/alerts?resolved=false response (single fetch, two filters).
const SYSTEM_ALERT_TYPE_NAMES = ['sync_error', 'api_failure', 'integration_warning'];
const BOOKING_CONFLICT_TYPE_NAME = 'booking_conflict';

const countAlertsByType = (alerts, typeNames) => {
  const typeSet = Array.isArray(typeNames) ? typeNames : [typeNames];
  return alerts.filter(a => typeSet.includes(a.alertType?.name || '')).length;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Dashboard states
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeNavItem, setActiveNavItem] = useState('overview');

  // Today's Operations Metrics
  const [todayMetrics, setTodayMetrics] = useState({
    checkInsToday: 0,
    checkOutsToday: 0,
    pendingTasks: 0,
  });

  // Unresolved alerts — shared TanStack cache with /alerts management page.
  // Resolving on that page invalidates ['alerts'] → card counts refresh instantly.
  const { alerts: unresolvedAlerts } = useUnresolvedAlerts();

  const systemAlertsCount = useMemo(
    () => countAlertsByType(unresolvedAlerts, SYSTEM_ALERT_TYPE_NAMES),
    [unresolvedAlerts]
  );
  const bookingConflictsCount = useMemo(
    () => countAlertsByType(unresolvedAlerts, BOOKING_CONFLICT_TYPE_NAME),
    [unresolvedAlerts]
  );

  // System Health Metrics
  const [systemHealth, setSystemHealth] = useState({
    lastSyncTime: null,
    lastSyncSuccess: false,
    channexStatus: 'unknown',
    gathernStatus: 'unknown',
    totalProperties: 0,
    activeBookings: 0
  });

  // Authentication check
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      console.warn('No token found, redirecting to login');
      navigate('/login');
      return;
    }

    // Admin-only access
    if (user && user.role !== 'admin') {
      console.warn('Non-admin user accessing admin dashboard');
      navigate('/dashboard');
      return;
    }
  }, [navigate, user]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch properties
        const propertiesData = await secureApiService.getProperties();
        setProperties(propertiesData);

        // "Today" is asked as a BUSINESS QUESTION and answered by the server in
        // Asia/Riyadh — this page must never compute a midnight boundary itself.
        // It used to (`new Date(); setHours(0,0,0,0)`), which meant a browser
        // outside Saudi asked for its OWN day: on 2026-07-21 at 01:29 Riyadh the
        // arrivals card showed yesterday's guest while the arrival-readiness card,
        // resolved server-side, correctly showed none.
        // See tasks/business-day-boundary-sweep.md and be/utils/businessCalendar.js.

        // Fetch today's arrivals — counted on the EFFECTIVE arrival time (the
        // ops-granted ETA, else the property policy), NOT the booking's paper date.
        // `checkIn` used to be passed here, which advertised a guest the team had
        // already let in the previous evening (prod, HM-719A4065, 2026-09-01) and
        // would miss one walking in tonight on tomorrow's dates. Same resolver as
        // the readiness card below, so the two cannot disagree.
        // See tasks/arrivals-tile-effective-arrival.md.
        const checkInsResponse = await axios.get('/api/v1/bookings', {
          params: {
            businessDate: 'today',
            businessDateField: 'arrival'
          },
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        const checkInsData = Array.isArray(checkInsResponse.data)
          ? checkInsResponse.data
          : checkInsResponse.data.bookings || [];
        const checkIns = checkInsData.length;

        // Fetch today's departures — effective time, same reasoning as arrivals
        // above. A granted late checkout rolling past midnight belongs to the day
        // the guest actually leaves (prod, HM-FC3BBD21).
        const checkOutsResponse = await axios.get('/api/v1/bookings', {
          params: {
            businessDate: 'today',
            businessDateField: 'departure'
          },
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        const checkOutsData = Array.isArray(checkOutsResponse.data)
          ? checkOutsResponse.data
          : checkOutsResponse.data.bookings || [];
        const checkOuts = checkOutsData.length;

        // Fetch active bookings — occupancy as the day began, i.e. guests already
        // in the property. Same question the old `activeOn` instant asked; only
        // the day it resolved to was wrong.
        const activeResponse = await axios.get('/api/v1/bookings', {
          params: {
            businessDate: 'today',
            businessDateField: 'occupied'
          },
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        const activeData = Array.isArray(activeResponse.data)
          ? activeResponse.data
          : activeResponse.data.bookings || [];
        const activeBookings = activeData.length;

        // Fetch tasks (defensive coding: handle API errors gracefully)
        let pendingTasks = 0;
        try {
          const tasksData = await taskApi.getTasks();
          const tasks = tasksData.data?.tasks || [];
          // Filter for pending/assigned tasks on frontend
          pendingTasks = tasks.filter(t =>
            t.status === 'pending' || t.status === 'assigned'
          ).length;
        } catch (error) {
          console.warn('⚠️ Tasks API error:', error.message);
          // Graceful fallback: continue with pendingTasks = 0
        }

        // Alerts are fetched via useUnresolvedAlerts() hook above (shared TanStack cache).

        // Fetch sync info
        const syncResponse = await axios.get('/api/v1/sync/info', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        const syncData = syncResponse.data;
        const lastSync = syncData.lastSync;

        setTodayMetrics({
          checkInsToday: checkIns,
          checkOutsToday: checkOuts,
          pendingTasks,
        });

        setSystemHealth({
          lastSyncTime: lastSync?.completedAt,
          lastSyncSuccess: lastSync?.status === 'completed',
          channexStatus: 'active', // TODO: Check actual Channex connection
          gathernStatus: 'active',  // TODO: Check actual Gathern connection
          totalProperties: propertiesData.length,
          activeBookings
        });

      } catch (error) {
        console.error("❌ Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'admin') {
      fetchDashboardData();

      // Refresh every 5 minutes
      const refreshInterval = setInterval(fetchDashboardData, 5 * 60 * 1000);
      return () => clearInterval(refreshInterval);
    }
  }, [user]);

  // Format sync time
  const formatSyncTime = (syncTime) => {
    if (!syncTime) return 'لم تتم المزامنة';

    const lastSyncDate = new Date(syncTime);
    const timeDiff = Date.now() - lastSyncDate.getTime();
    const minutes = Math.floor(timeDiff / 60000);

    if (minutes < 1) return 'آخر مزامنة: الآن';
    if (minutes < 60) return `آخر مزامنة: منذ ${minutes} دقيقة`;

    const hours = Math.floor(minutes / 60);
    return `آخر مزامنة: منذ ${hours} ساعة`;
  };

  if (loading) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem} headerTitle="لوحة التحكم">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="لوحة العمليات"
    >
      <div className="admin-dashboard-container">
        <h3 style={{ marginBottom: '25px', color: '#471396' }}>
          نظرة عامة على النظام
        </h3>

        {/* Today's Operations - Primary Section */}
        <div className="dashboard-primary-section" style={{ marginBottom: '40px' }}>
          <h4 style={{ marginBottom: '20px', color: '#471396', fontSize: '20px' }}>
            نشاط اليوم
          </h4>

          <div className="stats-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            {/* Check-ins Today */}
            <div className="stat-card" style={{
              borderLeft: '5px solid #28a745',
              padding: '25px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <i className="bi bi-box-arrow-in-down" style={{ fontSize: '36px', color: '#28a745' }}></i>
                <div>
                  <h2 style={{ color: '#28a745', fontSize: '48px', margin: '0' }}>
                    {todayMetrics.checkInsToday}
                  </h2>
                  <p style={{ marginBottom: '0', fontSize: '16px', fontWeight: '500' }}>وصول ضيوف</p>
                  <small style={{ color: '#666' }}>تسجيل دخول اليوم</small>
                </div>
              </div>
            </div>

            {/* Check-outs Today */}
            <div className="stat-card" style={{
              borderLeft: '5px solid #dc3545',
              padding: '25px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <i className="bi bi-box-arrow-up" style={{ fontSize: '36px', color: '#dc3545' }}></i>
                <div>
                  <h2 style={{ color: '#dc3545', fontSize: '48px', margin: '0' }}>
                    {todayMetrics.checkOutsToday}
                  </h2>
                  <p style={{ marginBottom: '0', fontSize: '16px', fontWeight: '500' }}>مغادرة ضيوف</p>
                  <small style={{ color: '#666' }}>تسجيل خروج اليوم</small>
                </div>
              </div>
            </div>

            {/* Pending Tasks */}
            <div className="stat-card mobile-hide" style={{
              borderLeft: '5px solid #ffc107',
              padding: '25px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <i className="bi bi-list-check" style={{ fontSize: '36px', color: '#ffc107' }}></i>
                <div>
                  <h2 style={{ color: '#ffc107', fontSize: '48px', margin: '0' }}>
                    {todayMetrics.pendingTasks}
                  </h2>
                  <p style={{ marginBottom: '0', fontSize: '16px', fontWeight: '500' }}>مهام معلقة</p>
                  <small style={{ color: '#666' }}>تنظيف وصيانة</small>
                </div>
              </div>
            </div>

            {/* System Alerts */}
            <div className="stat-card mobile-hide" style={{
              borderLeft: '5px solid #17a2b8',
              padding: '25px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <i className="bi bi-exclamation-triangle" style={{ fontSize: '36px', color: '#17a2b8' }}></i>
                <div>
                  <h2 style={{ color: '#17a2b8', fontSize: '48px', margin: '0' }}>
                    {systemAlertsCount}
                  </h2>
                  <p style={{ marginBottom: '0', fontSize: '16px', fontWeight: '500' }}>تنبيهات النظام</p>
                  <small style={{ color: '#666' }}>أخطاء مزامنة وAPI</small>
                </div>
              </div>
            </div>

            {/* Booking Conflicts */}
            <div className="stat-card" style={{
              borderLeft: '5px solid #cc0000',
              padding: '25px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <i className="bi bi-calendar-x" style={{ fontSize: '36px', color: '#cc0000' }}></i>
                <div>
                  <h2 style={{ color: '#cc0000', fontSize: '48px', margin: '0' }}>
                    {bookingConflictsCount}
                  </h2>
                  <p style={{ marginBottom: '0', fontSize: '16px', fontWeight: '500' }}>تعارضات حجز</p>
                  <small style={{ color: '#666' }}>حجوزات متداخلة غير محلولة</small>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* System Health - Secondary Section */}
        <div className="dashboard-secondary-section mobile-hide" style={{ marginBottom: '40px' }}>
          <h4 style={{ marginBottom: '20px', color: '#471396', fontSize: '20px' }}>
            صحة النظام
          </h4>

          <div className="stats-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px'
          }}>
            {/* Last Sync */}
            <div className="stat-card" style={{
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <i className="bi bi-arrow-repeat" style={{ fontSize: '24px', color: '#6c757d' }}></i>
                <h5 style={{ margin: 0, fontSize: '16px', color: '#6c757d' }}>آخر مزامنة</h5>
              </div>
              <p style={{ margin: '5px 0', fontSize: '14px', fontWeight: '500' }}>
                {formatSyncTime(systemHealth.lastSyncTime)}
              </p>
              {systemHealth.lastSyncSuccess && (
                <i className="bi bi-check-circle-fill" style={{ color: '#28a745', fontSize: '18px' }}></i>
              )}
            </div>

            {/* Channex Status */}
            <div className="stat-card" style={{
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <i className="bi bi-globe" style={{ fontSize: '24px', color: '#6c757d' }}></i>
                <h5 style={{ margin: 0, fontSize: '16px', color: '#6c757d' }}>Channex API</h5>
              </div>
              <p style={{ margin: '5px 0', fontSize: '14px', fontWeight: '500' }}>
                {systemHealth.channexStatus === 'active' ? 'متصل' : 'غير متصل'}
              </p>
              <i className="bi bi-check-circle-fill" style={{ color: '#28a745', fontSize: '18px' }}></i>
            </div>

            {/* Total Properties */}
            <div className="stat-card" style={{
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <i className="bi bi-building" style={{ fontSize: '24px', color: '#6c757d' }}></i>
                <h5 style={{ margin: 0, fontSize: '16px', color: '#6c757d' }}>إجمالي العقارات</h5>
              </div>
              <h3 style={{ margin: '5px 0', fontSize: '32px', color: '#471396' }}>
                {systemHealth.totalProperties}
              </h3>
            </div>

            {/* Active Bookings */}
            <div className="stat-card" style={{
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <i className="bi bi-calendar-check" style={{ fontSize: '24px', color: '#6c757d' }}></i>
                <h5 style={{ margin: 0, fontSize: '16px', color: '#6c757d' }}>حجوزات نشطة</h5>
              </div>
              <h3 style={{ margin: '5px 0', fontSize: '32px', color: '#471396' }}>
                {systemHealth.activeBookings}
              </h3>
            </div>
          </div>
        </div>

        {/* Door readiness for today's arrivals — the pull twin of the 06:50
            morning assertion (tasks/lock-health-assertion-phase4.md). Placed
            ABOVE the check-in monitor deliberately: "can the guest get in" is
            the first operational question of the day, and this card is where
            the two will merge into one arrival row. */}
        <div style={{ marginTop: '30px' }}>
          <ErrorBoundary>
            <ArrivalReadinessCard />
          </ErrorBoundary>
        </div>

        {/* Departure readiness — directly BELOW arrivals so the page reads arrivals → departures,
            the guest journey in the order ops live it. Its own ErrorBoundary: the two cards answer
            independent questions, so one failing must never take the other off the screen.
            Plan: tasks/checkout-message-automation.md §3.9 */}
        <div style={{ marginTop: '30px' }}>
          <ErrorBoundary>
            <DepartureReadinessCard />
          </ErrorBoundary>
        </div>

        {/* Smart Locks fleet awareness table (Phase 1 — admin only) */}
        <div style={{ marginTop: '30px' }}>
          <ErrorBoundary>
            <AdminDashboardLockTable />
          </ErrorBoundary>
        </div>

      </div>
    </DesktopLayout>
  );
};

export default AdminDashboard;
